import React, { useEffect, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  TbBuildingFactory2,
  TbEdit,
  TbEye,
  TbMapPin,
  TbPackage,
  TbPlus,
  TbSearch,
  TbTrash,
  TbUser,
} from "react-icons/tb";
import api from "../api/axios";
import "../assets/styles/directory-tables.css";
import "../assets/styles/vendor-management.css";

const PAGE_NAME = "Vendors";
const PAGE_SIZE = 15;
const headers = (action) => ({ "x-page-name": PAGE_NAME, "x-action": action });

const emptyForm = {
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  alternate_phone: "",
  gst_no: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  country: "India",
  pincode: "",
  specializations: "",
  notes: "",
  status: "active",
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const getCustomer = (order) => {
  const snapshot = order?.customer_snapshot;
  if (!snapshot) return {};
  if (typeof snapshot === "object") return snapshot;
  try {
    return JSON.parse(snapshot);
  } catch {
    return {};
  }
};

const Vendors = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [ordersPagination, setOrdersPagination] = useState({ page: 1, total: 0, totalPages: 1 });

  const showError = (error, fallback) => {
    if (error.response?.status !== 403) {
      toast.error(error.response?.data?.message || fallback);
    }
  };

  const fetchVendors = async (page = 1, term = debouncedSearch, selectedStatus = status) => {
    try {
      setLoading(true);
      const response = await api.get("/admin/orders/vendors", {
        params: {
          page,
          limit: PAGE_SIZE,
          search: term || undefined,
          status: selectedStatus || undefined,
        },
        headers: headers("view"),
      });
      setVendors(response.data?.data || []);
      setPagination(response.data?.pagination || { page, total: 0, totalPages: 1 });
    } catch (error) {
      showError(error, "Failed to load vendors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchVendors(1, debouncedSearch, status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, status]);

  const openCreate = () => {
    setEditingVendor(null);
    setForm({ ...emptyForm });
    setFormOpen(true);
  };

  const openEdit = (vendor) => {
    setEditingVendor(vendor);
    setForm(
      Object.keys(emptyForm).reduce(
        (values, key) => ({ ...values, [key]: vendor[key] ?? emptyForm[key] }),
        {}
      )
    );
    setFormOpen(true);
  };

  const saveVendor = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Vendor name is required.");
    try {
      setSaving(true);
      if (editingVendor) {
        await api.put(`/admin/orders/vendors/${editingVendor.vendor_id}`, form, {
          headers: headers("edit"),
        });
        toast.success("Vendor updated.");
      } else {
        await api.post("/admin/orders/vendors", form, { headers: headers("add") });
        toast.success("Vendor created.");
      }
      setFormOpen(false);
      setEditingVendor(null);
      setForm({ ...emptyForm });
      await fetchVendors(pagination.page);
    } catch (error) {
      showError(error, "Failed to save vendor.");
    } finally {
      setSaving(false);
    }
  };

  const updateVendorStatus = async (vendor, nextStatus) => {
    try {
      await api.put(
        `/admin/orders/vendors/${vendor.vendor_id}`,
        { status: nextStatus },
        { headers: headers("edit") }
      );
      setVendors((items) =>
        items.map((item) =>
          item.vendor_id === vendor.vendor_id ? { ...item, status: nextStatus } : item
        )
      );
      toast.success(`Vendor marked ${nextStatus}.`);
    } catch (error) {
      showError(error, "Failed to update vendor status.");
    }
  };

  const deleteVendor = (vendor) => {
    confirmDialog({
      header: "Delete vendor",
      message: `Delete ${vendor.name}? Vendors with associated orders must be marked inactive instead.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await api.delete(`/admin/orders/vendors/${vendor.vendor_id}`, {
            headers: headers("delete"),
          });
          toast.success("Vendor deleted.");
          await fetchVendors(pagination.page);
        } catch (error) {
          showError(error, "Failed to delete vendor.");
        }
      },
    });
  };

  const fetchVendorOrders = async (vendor, page = 1) => {
    try {
      setOrdersLoading(true);
      const response = await api.get(`/admin/orders/vendors/${vendor.vendor_id}/orders`, {
        params: { page, limit: 10 },
        headers: headers("view"),
      });
      setVendorOrders(response.data?.data || []);
      setOrdersPagination(response.data?.pagination || { page, total: 0, totalPages: 1 });
    } catch (error) {
      showError(error, "Failed to load vendor orders.");
    } finally {
      setOrdersLoading(false);
    }
  };

  const openVendor = (vendor) => {
    setSelectedVendor(vendor);
    setVendorOrders([]);
    setViewOpen(true);
    fetchVendorOrders(vendor, 1);
  };

  return (
    <section className="admin-page vendor-page">
      <ConfirmDialog />
      <div className="page-header users-hero-header">
        <div>
          <p>Printing partner directory</p>
          <h1>Vendors</h1>
        </div>
        <button type="button" className="primary-action" onClick={openCreate}>
          <TbPlus /> Add vendor
        </button>
      </div>

      <div className="page-card vendor-directory-card">
        <div className="vendor-toolbar">
          <div>
            <h2>Printing vendors</h2>
            <p>{pagination.total} vendor{pagination.total === 1 ? "" : "s"} found</p>
          </div>
          <div className="vendor-filters">
            <label><TbSearch /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, code, contact, city..." /></label>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All vendors</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className={`directory-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="directory-data-table vendor-data-table">
            <thead><tr><th>Vendor</th><th>Contact</th><th>Location</th><th>Specializations</th><th>Orders</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {!loading && vendors.length === 0 && <tr><td colSpan="7" className="directory-table-empty"><TbBuildingFactory2 /><strong>No vendors found</strong><span>Add a printing vendor or change the search filters.</span></td></tr>}
              {vendors.map((vendor) => (
                <tr key={vendor.vendor_id}>
                  <td className="directory-person-cell"><div className="directory-person"><span className="directory-avatar"><TbBuildingFactory2 /></span><div><strong>{vendor.name}</strong><span>{vendor.vendor_code}</span><small>{vendor.gst_no || "GST not provided"}</small></div></div></td>
                  <td className="vendor-contact-cell"><strong>{vendor.contact_person || "No contact person"}</strong><span>{vendor.phone || vendor.email || "No contact details"}</span><small>{vendor.phone && vendor.email ? vendor.email : ""}</small></td>
                  <td className="directory-address-cell"><strong>{[vendor.city, vendor.state].filter(Boolean).join(", ") || "Not provided"}</strong><span>{vendor.pincode || vendor.country || ""}</span></td>
                  <td className="vendor-specialization-cell">{vendor.specializations || "General printing"}</td>
                  <td><button type="button" className="vendor-order-count" onClick={() => openVendor(vendor)}><TbPackage /> {vendor.order_count || 0}</button></td>
                  <td><select className={`directory-status-select status-${vendor.status}`} value={vendor.status} onChange={(event) => updateVendorStatus(vendor, event.target.value)}><option value="active">Active</option><option value="inactive">Inactive</option></select></td>
                  <td className="directory-actions-cell"><div className="directory-row-actions"><button type="button" title="View vendor" onClick={() => openVendor(vendor)}><TbEye /></button><button type="button" title="Edit vendor" onClick={() => openEdit(vendor)}><TbEdit /></button><button type="button" title="Delete vendor" className="danger" onClick={() => deleteVendor(vendor)}><TbTrash /></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="directory-pagination">
          <button type="button" className="directory-pagination-direction" disabled={pagination.page <= 1 || loading} onClick={() => fetchVendors(pagination.page - 1)}>Previous</button>
          <strong>Page {pagination.page} of {pagination.totalPages}</strong>
          <button type="button" className="directory-pagination-direction" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => fetchVendors(pagination.page + 1)}>Next</button>
        </div>
      </div>

      <Sidebar visible={formOpen} position="right" onHide={() => !saving && setFormOpen(false)} className="premium-user-sidebar vendor-form-sidebar">
        <form className="vendor-form" onSubmit={saveVendor}>
          <header><span>{editingVendor ? "Edit vendor" : "New vendor"}</span><h2>{editingVendor?.name || "Add printing partner"}</h2><p>Vendor access is internal only. No login account will be created.</p></header>
          <div className="vendor-form-grid">
            <label className="wide"><span>Vendor name *</span><input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
            <label><span>Contact person</span><input value={form.contact_person} onChange={(event) => setForm({ ...form, contact_person: event.target.value })} /></label>
            <label><span>Status</span><select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
            <label><span>Email</span><input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
            <label><span>Phone</span><input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
            <label><span>Alternate phone</span><input value={form.alternate_phone} onChange={(event) => setForm({ ...form, alternate_phone: event.target.value })} /></label>
            <label><span>GST number</span><input value={form.gst_no} onChange={(event) => setForm({ ...form, gst_no: event.target.value })} /></label>
            <label className="wide"><span>Address line 1</span><input value={form.address_line1} onChange={(event) => setForm({ ...form, address_line1: event.target.value })} /></label>
            <label className="wide"><span>Address line 2</span><input value={form.address_line2} onChange={(event) => setForm({ ...form, address_line2: event.target.value })} /></label>
            <label><span>City</span><input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} /></label>
            <label><span>State</span><input value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })} /></label>
            <label><span>Country</span><input value={form.country} onChange={(event) => setForm({ ...form, country: event.target.value })} /></label>
            <label><span>Pincode</span><input value={form.pincode} onChange={(event) => setForm({ ...form, pincode: event.target.value })} /></label>
            <label className="wide"><span>Printing specializations</span><textarea rows="3" value={form.specializations} onChange={(event) => setForm({ ...form, specializations: event.target.value })} placeholder="Foil printing, digital printing, laser cutting..." /></label>
            <label className="wide"><span>Internal notes</span><textarea rows="4" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></label>
          </div>
          <footer><button type="button" className="ghost-action bottonborder" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</button><button type="submit" className="primary-action" disabled={saving}>{saving ? "Saving..." : editingVendor ? "Update vendor" : "Add vendor"}</button></footer>
        </form>
      </Sidebar>

      <Sidebar visible={viewOpen} position="right" onHide={() => setViewOpen(false)} className="premium-user-sidebar vendor-view-sidebar">
        {selectedVendor && <div className="vendor-view">
          <header><span className="vendor-view-icon"><TbBuildingFactory2 /></span><div><small>{selectedVendor.vendor_code}</small><h2>{selectedVendor.name}</h2><p>{selectedVendor.specializations || "General printing vendor"}</p></div></header>
          <div className="vendor-info-grid"><div><TbUser /><span><small>Contact</small><strong>{selectedVendor.contact_person || "Not provided"}</strong><p>{selectedVendor.phone || selectedVendor.email || "No contact details"}</p></span></div><div><TbMapPin /><span><small>Location</small><strong>{[selectedVendor.city, selectedVendor.state].filter(Boolean).join(", ") || "Not provided"}</strong><p>{selectedVendor.address_line1 || selectedVendor.country}</p></span></div></div>
          <section className="vendor-orders-section"><div><h3>Associated orders</h3><span>{ordersPagination.total} production assignment{ordersPagination.total === 1 ? "" : "s"}</span></div>
            {ordersLoading && <p className="vendor-orders-empty">Loading assigned orders...</p>}
            {!ordersLoading && vendorOrders.length === 0 && <p className="vendor-orders-empty">No orders have been assigned to this vendor.</p>}
            {vendorOrders.map((job) => { const customer = getCustomer(job.order); return <article key={job.production_job_id}><div><strong>{job.order?.order_number}</strong><span>{customer.name || "Customer"}</span><small>Assigned {formatDate(job.createdAt)}</small></div><div><span className={`status-badge ${job.order?.order_status}`}>{String(job.order?.order_status || "processing").replace(/_/g, " ")}</span><strong>{formatCurrency(job.order?.total_amount)}</strong></div><button type="button" onClick={() => navigate(`/orders?order_id=${job.order_id}`)}>View order</button></article>; })}
            {ordersPagination.totalPages > 1 && <footer><button type="button" disabled={ordersPagination.page <= 1 || ordersLoading} onClick={() => fetchVendorOrders(selectedVendor, ordersPagination.page - 1)}>Previous</button><span>{ordersPagination.page} / {ordersPagination.totalPages}</span><button type="button" disabled={ordersPagination.page >= ordersPagination.totalPages || ordersLoading} onClick={() => fetchVendorOrders(selectedVendor, ordersPagination.page + 1)}>Next</button></footer>}
          </section>
        </div>}
      </Sidebar>
    </section>
  );
};

export default Vendors;
